import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { http } from "./api";
import { toast } from 'sonner';

const CartCtx = createContext(null);
const KEY = "vlover_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; }
  });
  const [removed, setRemoved] = useState(() => { try { return JSON.parse(sessionStorage.getItem('vl_cart_undo')) || null; } catch { return null; } });
  const removedRef = useRef(removed);
  const undoLock = useRef(false);
  const [undoBusy, setUndoBusy] = useState(false);
  const saveRemoved = useCallback(snapshot => {
    removedRef.current = snapshot;
    setRemoved(snapshot);
    if (snapshot) sessionStorage.setItem('vl_cart_undo', JSON.stringify(snapshot));
    else sessionStorage.removeItem('vl_cart_undo');
  }, []);
  useEffect(() => {
    const sync = (e) => { if (e.key === KEY) { try { setItems(JSON.parse(e.newValue) || []); } catch { setItems([]); } } };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);
  const mutate = useCallback((change) => {
    const apply = () => {
      let current; try { current = JSON.parse(localStorage.getItem(KEY)) || []; } catch { current = []; }
      const next = change(current);
      const invalidUnits = next.some(item => !item.sample && (!Number.isInteger(item.packs) || item.packs < 1));
  const invalid = next.length > 50 || next.some(item => !item.sample && item.packs > 500);
      const reducing = next.reduce((sum, item) => sum + (item.sample ? 1 : item.packs), 0) < current.reduce((sum, item) => sum + (item.sample ? 1 : item.packs), 0);
      if (invalidUnits || (invalid && !reducing)) {
        toast.error('Maximaal 500 verkoopeenheden per orderregel en 50 orderregels per bestelling. Neem contact op voor grotere projecten.');
        return false;
      }
      localStorage.setItem(KEY, JSON.stringify(next));
      setItems(next);
      return true;
    };
    // Serialize cart mutations across tabs; read the latest persisted basket inside the lock.
    if (navigator.locks) return navigator.locks.request(KEY, apply);
    return apply();
  }, []);

  const add = useCallback((product_id, packs, meta = {}) => {
    return mutate((cur) => {
      const ex = cur.find((i) => i.product_id === product_id && !i.sample);
      if (ex) return cur.map((i) => (i.product_id === product_id && !i.sample ? { ...i, packs, ...meta } : i));
      return [...cur, { product_id, packs, ...meta }];
    });
  }, [mutate]);
  const ensure = useCallback((product_id, packs, meta = {}) => mutate(cur => {
    const existing = cur.find(x => x.product_id === product_id && !x.sample);
    return existing ? cur.map(x => x === existing ? { ...x, ...meta, packs: Math.max(x.packs, packs) } : x) : [...cur, { product_id, packs, ...meta }];
  }), [mutate]);
  const addSample = (product_id) => mutate((cur) => (cur.some((i) => i.product_id === product_id && i.sample) ? cur : [...cur, { product_id, packs: 0, sample: true }]));
  const setPacks = (product_id, packs) => mutate((cur) => cur.map((i) => (i.product_id === product_id && !i.sample ? { ...i, packs } : i)));
  const update = (product_id, patch) => mutate((cur) => cur.map((i) => (i.product_id === product_id && !i.sample ? { ...i, ...patch } : i)));
  const remove = (product_id, sample = false) => mutate((cur) => cur.filter((i) => !(i.product_id === product_id && !!i.sample === sample)));
  const clear = () => mutate(() => []);
  const emptyWithUndo = useCallback(() => mutate(current => { if (current.length) saveRemoved(current); return []; }), [mutate, saveRemoved]);
  const undoEmpty = useCallback(async () => {
    const snapshot = removedRef.current;
    if (undoLock.current || !snapshot?.length) return;
    undoLock.current = true;
    removedRef.current = null;
    setUndoBusy(true);
    try {
      const success = await mutate(current => [...snapshot.map(item => ({ ...item })), ...current.filter(item => !snapshot.some(old => item.product_id === old.product_id && !!item.sample === !!old.sample))]);
      if (success === false) removedRef.current = snapshot;
      else { saveRemoved(null); toast.success('Artikelen teruggezet in je winkelwagen.'); }
    } catch (error) { removedRef.current = snapshot; toast.error('Terugzetten is niet gelukt. Probeer het opnieuw.'); }
    finally { undoLock.current = false; setUndoBusy(false); }
  }, [mutate, saveRemoved]);
  const completePaid = useCallback((orderId) => {
    let pending; try { pending = JSON.parse(localStorage.getItem('vl_pending_cart')); } catch { return; }
    if (pending?.id !== orderId || !Array.isArray(pending.items)) return;
    return Promise.resolve(mutate(cur => cur.filter(item => !pending.items.some(old => old.product_id === item.product_id && !!old.sample === !!item.sample && old.packs === item.packs && old.area === item.area && old.loss === item.loss)))).then(() => {
      saveRemoved(null);
      localStorage.removeItem('vl_pending_cart');
      let last; try { last = JSON.parse(localStorage.getItem('vl_last_order')); } catch { return; }
      if (last?.id === orderId) localStorage.removeItem('vl_last_order');
    });
  }, [mutate, saveRemoved]);
  const samples = items.filter((i) => i.sample).length;
  const count = items.reduce((s, i) => s + (i.sample ? 1 : i.packs || 0), 0);
  return <CartCtx.Provider value={{ items, add, ensure, addSample, setPacks, update, remove, clear, emptyWithUndo, undoEmpty, removed, undoBusy, completePaid, count, samples }}>{children}</CartCtx.Provider>;
}

export const useCart = () => useContext(CartCtx);

const AuthCtx = createContext(null);
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const refresh = useCallback(async () => {
    try { const { data } = await http.get("/auth/me"); setUser(data); } catch { setUser(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  const logout = async () => { await http.post("/auth/logout"); setUser(false); };
  return <AuthCtx.Provider value={{ user, setUser, refresh, logout }}>{children}</AuthCtx.Provider>;
}
export const useAuth = () => useContext(AuthCtx);
